import { rememberCtaClick } from "Main/cta-attribution";

/**
 * Markup opts in with `data-gtm-event`, which names the event, alongside
 * `data-gtm-category` and `data-gtm-label` for how it reads in a report. A
 * `data-cta-location` marks a call to action the contact form should be able to
 * trace itself back to, and is what gets remembered.
 */
const TRACKED = "[data-gtm-event]";

export interface ClickTrackingPayload {
  ctaLocation?: string;
  event: string;
  eventCategory: string;
  eventLabel: string;
  eventPageUrl: string;
}

/**
 * The same shape phone number clicks already push — see phoneNumberTracking —
 * so a report reads both through the variables GTM has mapped rather than
 * gaining a second vocabulary for the same idea.
 */
export function clickTrackingPayload(
  element: HTMLElement
): ClickTrackingPayload | null {
  const { gtmEvent, gtmCategory, gtmLabel, ctaLocation } = element.dataset;

  if (!gtmEvent) {
    return null;
  }

  return {
    event: gtmEvent,
    eventCategory: gtmCategory || "",
    eventLabel: gtmLabel || "",
    ...(ctaLocation ? { ctaLocation } : {}),
    eventPageUrl: window.location.href,
  };
}

/**
 * One delegated listener for every tracked click on the page.
 *
 * Delegated rather than bound per component because these clicks come from
 * three different places: theme templates, Vue templates, and the anchors
 * inside translated strings that arrive through v-html and have no component to
 * bind to. A listener on the document reaches all three, and it is how the
 * contact form's own open event has always worked.
 */
let isListening = false;

export function initClickTracking(): void {
  // A second listener would report every click twice, and a doubled count is
  // near-impossible to spot in a report — so calling this again does nothing.
  if (isListening) {
    return;
  }

  isListening = true;

  document.addEventListener("click", (event: Event) => {
    const { target } = event;

    if (!(target instanceof Element)) {
      return;
    }

    // closest, not the target itself: the CTAs carry an icon, and a click on it
    // makes the target the svg rather than the element holding the attributes.
    const element = target.closest<HTMLElement>(TRACKED);

    if (!element) {
      return;
    }

    const payload = clickTrackingPayload(element);

    if (!payload) {
      return;
    }

    if (payload.ctaLocation) {
      rememberCtaClick(payload.ctaLocation);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  });
}
